/**
 * HTML utilities
 *
 * JS file instead of TS to support reuse by `scripts/*.js`
 */

import nodeHtmlParser from 'node-html-parser';

/**
 * Parse HTML and turn it into a queryable DOM tree.
 * Thin wrapper around `nodeHtmlParser.parse` with some safeguards.
 *
 * @param {string} html
 * @returns {HTMLElement}
 */
export const parseHtml = (html) => {
  return nodeHtmlParser.parse(
    html
      // `node-html-parser` doesn't like closing `</style>` tags spread across multiple lines, of which
      // `@number-flow/react` is a notable offender, even though it's technically valid HTML.
      // While the parse stage won't fail, malformed markup will be introduced.
      //
      // For example:
      //
      // ```
      // <style></style
      // >
      // ```
      //
      // Interestingly, it appears to support other closing
      // tags, like `div` and `span`, in this format.
      //
      // To work around this (and avoid seeking another more-robust HTML parser, like
      // `parse5`, perhaps), we convert all closing tags to single-line tags.
      //
      // @see 13.1.2.2(4) https://html.spec.whatwg.org/multipage/syntax.html#end-tags
      .replaceAll(/<(\/\S+)\s+>/g, '<$1>'), // `</style\n>` -> `</style>`
  );
};

/**
 * Sanitize potentially malformed HTML. This is useful when you need to
 * ingest user input but want to avoid React hydration errors.
 *
 * For example, the Contentful CMS blog accepts arbitrary unvalidated HTML in the form of
 * a plain text field. While ideally we would never publish invalid HTML, we
 * need to guard against the possibility of it happening.
 *
 * @param {string} html
 * @returns {string}
 */
export const sanitizeHtml = (html) => {
  return parseHtml(html).toString();
};
